import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchThreads } from "@/lib/api/threads";
import { THREAD_CHUNK_SIZE, THREAD_MAX_OFFSET } from "@/lib/messages/messages-constants";
import { getThreadRowId, normalizeApiThreadRow } from "@/lib/messages/messages-helpers";

// Bound on cached rows: chunk 0 plus everything near the viewport survives, far-away chunks are
// evicted so an afternoon of scrolling a 50k-thread inbox doesn't hold 50k rows in memory.
const MAX_CACHED_ROWS = 600;
// Rows kept on each side of the desired range when evicting.
const EVICT_MARGIN_ROWS = 200;
// Max chunks coalesced into one request (4 × 50 = the server's max page size).
const MAX_CHUNKS_PER_FETCH = 4;
// Max concurrent range fetches; further chunks queue and are re-checked against the desired
// range when a slot frees, so a fast scrollbar drag doesn't fan out a request per passed chunk.
const MAX_CONCURRENT_FETCHES = 2;

const EMPTY_SNAPSHOT = Object.freeze({
  total: null,
  rowsByIndex: new Map(),
  indexByThreadId: new Map(),
  chunkGens: new Map(),
  generation: 0,
});

function chunkStartFor(index) {
  return Math.floor(Math.max(0, index) / THREAD_CHUNK_SIZE) * THREAD_CHUNK_SIZE;
}

function buildQueryKey(filters) {
  return JSON.stringify({
    // Server-side thread scope ("" = the default patient inbox, "groups" = WhatsApp group
    // chats). A scope switch swaps the whole population, so it must reset like a filter change.
    scope: String(filters?.scope || ""),
    locked: Boolean(filters?.locked),
    awaitingReply: Boolean(filters?.awaitingReply),
    whatsapp: Boolean(filters?.whatsapp),
    conversationType: String(filters?.conversationType || ""),
    whatsappBotId: String(filters?.whatsappBotId || ""),
    lastMessageFrom: String(filters?.lastMessageFrom || ""),
    lastMessageTo: String(filters?.lastMessageTo || ""),
    groups: String(filters?.groups || ""),
    // Merged WhatsApp group rows change the ROW COUNT, not just which rows match, so a flip
    // must reset the window exactly like a filter change — otherwise the sparse cache keeps
    // serving unmerged rows against a merged total.
    mergeGroups: Boolean(filters?.mergeGroups),
  });
}

/**
 * Sparse, index-addressed window over the server's thread list — the data half of the fully
 * virtual thread list. The server owns the ordering ({lastMessageAt desc, _id desc}) and the
 * exact `total`; this hook caches rows by their absolute offset, fetches missing chunk-aligned
 * ranges on demand, and invalidates by generation when the order may have shifted (WS bumps,
 * filter changes).
 *
 * Generation model: rows carry the generation of the fetch that produced them per chunk
 * (`chunkGens`). `refresh()` bumps the generation WITHOUT clearing rows — stale rows keep
 * rendering (stale-while-revalidate) while every chunk near the viewport refetches (the range
 * effect treats a stale chunkGen as missing); fresh responses overwrite in place. `reset()`
 * (filter change) clears everything and shows skeletons — stale rows would sit at wrong offsets
 * under a different filter.
 *
 * Not handled here by design: optimistic inserts (none exist today — the WS layer refuses to
 * inject unknown threads; a future implementation should overlay them as leading rows above
 * offset 0, never splice them into the index-keyed cache) and scroll anchoring across total
 * changes (accepted one-row shift; see VirtualThreadList).
 */
export function useThreadListWindow({ enabled = true, filters = null, seed = null, seedPending = false } = {}) {
  const [snap, setSnap] = useState(() => {
    const rows = Array.isArray(seed?.rows) ? seed.rows : [];
    const total = Number.isFinite(seed?.total) ? seed.total : null;
    if (!rows.length && total == null) return EMPTY_SNAPSHOT;
    const rowsByIndex = new Map();
    const indexByThreadId = new Map();
    for (let i = 0; i < rows.length; i++) {
      const row = normalizeApiThreadRow(rows[i], null);
      const tid = getThreadRowId(row);
      if (!tid || indexByThreadId.has(tid)) continue;
      rowsByIndex.set(rowsByIndex.size, row);
      indexByThreadId.set(tid, rowsByIndex.size - 1);
    }
    const chunkGens = new Map();
    // Mark only FULLY covered chunks (or the chunk that reaches `total`) as fetched — a
    // partially seeded chunk must stay stale so the range effect completes it.
    for (let c = 0; c < rowsByIndex.size; c += THREAD_CHUNK_SIZE) {
      const covered = Math.min(rowsByIndex.size, c + THREAD_CHUNK_SIZE) - c;
      if (covered === THREAD_CHUNK_SIZE || (total != null && c + covered >= total)) {
        chunkGens.set(c, 0);
      }
    }
    return { total, rowsByIndex, indexByThreadId, chunkGens, generation: 0 };
  });
  const snapRef = useRef(snap);
  snapRef.current = snap;

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Source of truth for "the current generation", bumped SYNCHRONOUSLY by reset/refresh so the
  // fetches they issue are tagged with the new generation before the snapshot state flushes.
  const generationRef = useRef(0);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const queryKey = buildQueryKey(filters);
  const queryKeyRef = useRef(queryKey);
  const desiredRangeRef = useRef({ start: 0, end: THREAD_CHUNK_SIZE - 1 });
  // chunkStart -> generation of the in-flight fetch covering it.
  const inFlightRef = useRef(new Map());
  const queueRef = useRef([]);
  const activeFetchesRef = useRef(0);
  // One stale-total repair per generation: a short mid-list page means the cached total drifted
  // (rows deleted/moved server-side) — refetch chunk 0 + total once rather than loop on holes.
  const totalRepairGenRef = useRef(-1);

  const fetchParams = useCallback((offset, limit, withTotal) => {
    const f = filtersRef.current || {};
    return {
      offset,
      limit,
      withTotal,
      scope: f.scope,
      locked: f.locked,
      awaitingReply: f.awaitingReply,
      whatsapp: f.whatsapp,
      conversationType: f.conversationType,
      whatsappBotId: f.whatsappBotId,
      lastMessageFrom: f.lastMessageFrom,
      lastMessageTo: f.lastMessageTo,
      groups: f.groups,
      mergeGroups: f.mergeGroups,
    };
  }, []);

  /** Write fetched rows at their absolute offsets; drop if the generation moved on. */
  const applyFetched = useCallback((offset, limit, response, generation, { withTotal } = {}) => {
    setSnap((prev) => {
      if (generation !== prev.generation) return prev;
      const apiRows = Array.isArray(response?.threads) ? response.threads : [];
      const rowsByIndex = new Map(prev.rowsByIndex);
      const indexByThreadId = new Map(prev.indexByThreadId);
      const chunkGens = new Map(prev.chunkGens);
      for (let i = 0; i < apiRows.length; i++) {
        const index = offset + i;
        const old = rowsByIndex.get(index);
        const row = normalizeApiThreadRow(apiRows[i], old);
        const tid = getThreadRowId(row);
        if (tid) {
          // A thread that moved between fetches must not render twice: clear its previous slot
          // (the hole shows a skeleton and heals if it's ever visible again).
          const prevIndex = indexByThreadId.get(tid);
          if (prevIndex != null && prevIndex !== index && rowsByIndex.get(prevIndex) &&
              getThreadRowId(rowsByIndex.get(prevIndex)) === tid) {
            rowsByIndex.delete(prevIndex);
          }
          indexByThreadId.set(tid, index);
        }
        rowsByIndex.set(index, row);
      }
      for (let c = chunkStartFor(offset); c < offset + limit; c += THREAD_CHUNK_SIZE) {
        chunkGens.set(c, generation);
      }
      let total = prev.total;
      if (withTotal && Number.isFinite(response?.page?.total)) total = response.page.total;

      // Evict far-away rows once the cache outgrows its bound (chunk 0 always survives — it backs
      // the top of the list and every refresh).
      if (rowsByIndex.size > MAX_CACHED_ROWS) {
        const { start, end } = desiredRangeRef.current;
        const keepFrom = start - EVICT_MARGIN_ROWS;
        const keepTo = end + EVICT_MARGIN_ROWS;
        for (const [index, row] of rowsByIndex) {
          if (index < THREAD_CHUNK_SIZE) continue;
          if (index >= keepFrom && index <= keepTo) continue;
          rowsByIndex.delete(index);
          const tid = getThreadRowId(row);
          if (tid && indexByThreadId.get(tid) === index) indexByThreadId.delete(tid);
          chunkGens.delete(chunkStartFor(index));
        }
      }
      return { ...prev, total, rowsByIndex, indexByThreadId, chunkGens };
    });
  }, []);

  const startFetch = useCallback(
    (offset, limit, { withTotal = false } = {}) => {
      // Any offset-0 fetch while the total is unknown doubles as the total fetch — the scroll
      // effect's plain range request and the hook's own seed request dedupe against each other,
      // so whichever wins must not leave `total` unresolved.
      if (offset === 0 && snapRef.current.total == null) withTotal = true;
      const generation = generationRef.current;
      for (let c = offset; c < offset + limit; c += THREAD_CHUNK_SIZE) {
        inFlightRef.current.set(c, generation);
      }
      activeFetchesRef.current += 1;
      fetchThreads(fetchParams(offset, limit, withTotal))
        .then((response) => {
          applyFetched(offset, limit, response, generation, { withTotal });
          // Short mid-list page ⇒ the cached total no longer matches the server — repair once.
          const returned = Array.isArray(response?.threads) ? response.threads.length : 0;
          const total = snapRef.current.total;
          if (
            !withTotal &&
            returned < limit &&
            total != null &&
            offset + returned < total &&
            generation === generationRef.current &&
            totalRepairGenRef.current !== generation
          ) {
            totalRepairGenRef.current = generation;
            startFetchRef.current(0, THREAD_CHUNK_SIZE, { withTotal: true });
          }
        })
        .catch(() => {
          // Dropped silently: holes keep their skeletons and the range effect retries on the next
          // scroll/generation change; the page-level refresh cadence is the durable retry.
        })
        .finally(() => {
          activeFetchesRef.current -= 1;
          for (let c = offset; c < offset + limit; c += THREAD_CHUNK_SIZE) {
            if (inFlightRef.current.get(c) === generation) inFlightRef.current.delete(c);
          }
          pumpRef.current();
        });
    },
    [applyFetched, fetchParams],
  );
  const startFetchRef = useRef(startFetch);
  startFetchRef.current = startFetch;

  /** Dequeue desired chunks into at most MAX_CONCURRENT_FETCHES coalesced requests. */
  const pump = useCallback(() => {
    if (!enabledRef.current) return;
    const snapNow = snapRef.current;
    const generation = generationRef.current;
    const { start, end } = desiredRangeRef.current;
    while (activeFetchesRef.current < MAX_CONCURRENT_FETCHES && queueRef.current.length) {
      const next = queueRef.current.shift();
      const stillNeeded =
        next === 0 ||
        (next + THREAD_CHUNK_SIZE > start - EVICT_MARGIN_ROWS && next <= end + EVICT_MARGIN_ROWS);
      if (!stillNeeded) continue;
      if (snapNow.chunkGens.get(next) === generation) continue;
      if (inFlightRef.current.has(next) && inFlightRef.current.get(next) === generation) continue;
      // Coalesce immediately-consecutive queued chunks into one request.
      let count = 1;
      while (
        count < MAX_CHUNKS_PER_FETCH &&
        queueRef.current[0] === next + count * THREAD_CHUNK_SIZE
      ) {
        queueRef.current.shift();
        count += 1;
      }
      startFetchRef.current(next, count * THREAD_CHUNK_SIZE);
    }
  }, []);
  const pumpRef = useRef(pump);
  pumpRef.current = pump;

  /**
   * Note the viewport's desired row range and fetch whatever it needs. Idempotent; cheap to call
   * from a scroll-driven effect.
   */
  const requestRange = useCallback((start, end) => {
    if (!enabledRef.current) return;
    const snapNow = snapRef.current;
    const generation = generationRef.current;
    const total = snapNow.total;
    const clampedEnd = total != null ? Math.min(end, Math.max(0, total - 1)) : end;
    desiredRangeRef.current = { start: Math.max(0, start), end: Math.max(0, clampedEnd) };
    const wanted = [];
    for (let c = chunkStartFor(start); c <= clampedEnd; c += THREAD_CHUNK_SIZE) {
      if (total != null && c >= total) break;
      if (c > THREAD_MAX_OFFSET) break; // server 400s past its offset ceiling
      if (snapNow.chunkGens.get(c) === generation) continue;
      if (inFlightRef.current.get(c) === generation) continue;
      if (!queueRef.current.includes(c)) wanted.push(c);
    }
    if (wanted.length) {
      queueRef.current.push(...wanted);
      queueRef.current.sort((a, b) => a - b);
    }
    pumpRef.current();
  }, []);

  /**
   * Order may have shifted (WS bump): bump the generation so every chunk near the viewport
   * refetches, but keep rendering the stale rows until fresh ones land. When the caller already
   * holds fresh offset-0 rows (the overview response), they seed the new generation directly.
   */
  const refresh = useCallback(({ rows, total } = {}) => {
    queueRef.current = [];
    generationRef.current += 1;
    const generation = generationRef.current;
    setSnap((prev) => {
      const rowsByIndex = new Map(prev.rowsByIndex);
      const indexByThreadId = new Map(prev.indexByThreadId);
      const chunkGens = new Map(prev.chunkGens);
      let nextTotal = prev.total;
      if (Array.isArray(rows)) {
        for (let i = 0; i < rows.length; i++) {
          const old = rowsByIndex.get(i);
          const row = normalizeApiThreadRow(rows[i], old);
          const tid = getThreadRowId(row);
          if (tid) {
            const prevIndex = indexByThreadId.get(tid);
            if (prevIndex != null && prevIndex !== i && rowsByIndex.get(prevIndex) &&
                getThreadRowId(rowsByIndex.get(prevIndex)) === tid) {
              rowsByIndex.delete(prevIndex);
            }
            indexByThreadId.set(tid, i);
          }
          rowsByIndex.set(i, row);
        }
        // Only fully covered chunks (or the one reaching `total`) count as fetched — the
        // merged seed can end mid-chunk (see the pinned-row merge in refreshThreadData).
        const seededTotal = Number.isFinite(total) ? total : prev.total;
        for (let c = 0; c < rows.length; c += THREAD_CHUNK_SIZE) {
          const covered = Math.min(rows.length, c + THREAD_CHUNK_SIZE) - c;
          if (covered === THREAD_CHUNK_SIZE || (seededTotal != null && c + covered >= seededTotal)) {
            chunkGens.set(c, generation);
          }
        }
      }
      if (Number.isFinite(total)) nextTotal = total;
      return { ...prev, total: nextTotal, rowsByIndex, indexByThreadId, chunkGens, generation };
    });
    // No fresh rows in hand (filters active): refetch offset 0 + the exact total ourselves; the
    // range effect re-fetches whatever else is visible (its chunkGens are stale now).
    if (!Array.isArray(rows)) {
      startFetchRef.current(0, THREAD_CHUNK_SIZE, { withTotal: true });
    }
  }, []);

  /** Filter change: stale rows would sit at wrong offsets — clear to skeletons and start over. */
  const reset = useCallback(() => {
    queueRef.current = [];
    inFlightRef.current = new Map();
    generationRef.current += 1;
    const generation = generationRef.current;
    setSnap(() => ({
      total: null,
      rowsByIndex: new Map(),
      indexByThreadId: new Map(),
      chunkGens: new Map(),
      generation,
    }));
  }, []);

  useEffect(() => {
    if (queryKeyRef.current === queryKey) return;
    queryKeyRef.current = queryKey;
    reset();
  }, [queryKey, reset]);

  // Cold start / post-reset: nothing knows the total yet — fetch chunk 0 with it. Also the
  // durable retry when the initial fetch failed and a later render re-enables the list.
  //
  // `seedPending` says an overview request that WILL seed these exact rows and total is already in
  // flight. Without it, a cold mount fired this on the very first commit — before the overview it
  // duplicates had a chance to return — so the same 50 enriched rows and the same full-collection
  // count were fetched twice, back to back, on every cold load of the Messages page.
  const totalKnown = snap.total != null;
  useEffect(() => {
    if (!enabled || totalKnown || seedPending) return;
    if (inFlightRef.current.get(0) === generationRef.current) return;
    startFetchRef.current(0, THREAD_CHUNK_SIZE, { withTotal: true });
  }, [enabled, totalKnown, seedPending, queryKey, snap.generation]);

  const patchRow = useCallback((threadId, updater) => {
    const tid = String(threadId || "");
    if (!tid) return;
    setSnap((prev) => {
      const index = prev.indexByThreadId.get(tid);
      if (index == null) return prev;
      const row = prev.rowsByIndex.get(index);
      if (!row || getThreadRowId(row) !== tid) return prev;
      const next = typeof updater === "function" ? updater(row) : updater;
      if (!next || next === row) return prev;
      const rowsByIndex = new Map(prev.rowsByIndex);
      rowsByIndex.set(index, next);
      return { ...prev, rowsByIndex };
    });
  }, []);

  const patchRowsWhere = useCallback((predicate, updater) => {
    setSnap((prev) => {
      let rowsByIndex = null;
      for (const [index, row] of prev.rowsByIndex) {
        if (!predicate(row)) continue;
        const next = updater(row);
        if (!next || next === row) continue;
        if (!rowsByIndex) rowsByIndex = new Map(prev.rowsByIndex);
        rowsByIndex.set(index, next);
      }
      return rowsByIndex ? { ...prev, rowsByIndex } : prev;
    });
  }, []);

  /**
   * Local delete: mirror the server's shift (rows after the removed one move up by one) so the
   * list stays visually stable; the chunks past the removal point are re-marked stale so they
   * lazily refetch against the server's new offsets.
   */
  const removeThread = useCallback((threadId) => {
    const tid = String(threadId || "");
    if (!tid) return;
    setSnap((prev) => {
      const removedIndex = prev.indexByThreadId.get(tid);
      if (removedIndex == null) return prev;
      const rowsByIndex = new Map();
      const indexByThreadId = new Map();
      for (const [index, row] of prev.rowsByIndex) {
        if (index === removedIndex) continue;
        const nextIndex = index > removedIndex ? index - 1 : index;
        rowsByIndex.set(nextIndex, row);
        const rowTid = getThreadRowId(row);
        if (rowTid) indexByThreadId.set(rowTid, nextIndex);
      }
      const chunkGens = new Map();
      for (const [c, gen] of prev.chunkGens) {
        if (c < chunkStartFor(removedIndex)) chunkGens.set(c, gen);
      }
      const total = prev.total != null ? Math.max(0, prev.total - 1) : null;
      return { ...prev, total, rowsByIndex, indexByThreadId, chunkGens };
    });
  }, []);

  const getRow = useCallback((index) => snapRef.current.rowsByIndex.get(index), []);
  const getRowByThreadId = useCallback((threadId) => {
    const snapNow = snapRef.current;
    const index = snapNow.indexByThreadId.get(String(threadId || ""));
    if (index == null) return undefined;
    const row = snapNow.rowsByIndex.get(index);
    return row && getThreadRowId(row) === String(threadId || "") ? row : undefined;
  }, []);
  const loadedRowsSnapshot = useCallback(() => Array.from(snapRef.current.rowsByIndex.values()), []);

  return useMemo(
    () => ({
      total: snap.total,
      generation: snap.generation,
      snapshot: snap,
      getRow,
      getRowByThreadId,
      loadedRowsSnapshot,
      requestRange,
      refresh,
      reset,
      patchRow,
      patchRowsWhere,
      removeThread,
    }),
    [snap, getRow, getRowByThreadId, loadedRowsSnapshot, requestRange, refresh, reset, patchRow, patchRowsWhere, removeThread],
  );
}
